import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { isEqual } from "lodash";
import messages from "messages";
// components
import ScheduleEditor from "./components/schedule-editor/ScheduleEditor";
import ConfirmModal from "components/confirm-modal";
// hooks
import { useSchedule } from "../../../../../../../../contexts/ScheduleContext";
import { useCalendarTime } from "../../../../../../contexts/CalendarTimeContext";
import { useSavedTag } from "../../../../../../contexts/SavedTagContext";
import useToggleModal from "hooks/useToggleModal";
// utils
import createParsedAvailableTimePeriod from "../../../../../../../../utils/createParsedAvailableTimePeriod";
import getParsedAvailableTimePeriods from "../../utils/getParsedAvailableTimePeriods";
import { getAscendingSchedule } from "./utils/getAscendingSchedule";
// services
import postSchedule from "services/scheduler/postSchedule";
import { AmplitudePageName } from "utils/amplitude";

interface Props {
  show: boolean;
  onHide: () => void;
  analyticsPageName: AmplitudePageName;
}

/**
 * contains logic for editing schedule
 */
const ScheduleEditorContainer = ({
  onHide,
  show,
  analyticsPageName,
}: Props) => {
  const ref = useRef<Pick<HTMLFormElement, "checkValidity" | "reset"> | null>(
    null,
  );

  const { currentCalendarTimezone } = useCalendarTime();
  const { schedule, setSchedule } = useSchedule();
  const { setSavedTagProps } = useSavedTag();
  const [error, setError] = useState<any>();
  const [busy, setBusy] = useState(false);
  const [parsedAvailableTimePeriods, setParsedAvailableTimePeriods] = useState(
    getParsedAvailableTimePeriods(schedule, currentCalendarTimezone),
  );

  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const {
    show: showConfirmCancelModal,
    open: openConfirmCancelModal,
    close: closeConfirmCancelModal,
  } = useToggleModal(false);
  const {
    show: showConfirmSaveModal,
    open: openConfirmSaveModal,
    close: closeConfirmSaveModal,
  } = useToggleModal(false);

  // track parent schedule
  useEffect(() => {
    if (show) {
      setParsedAvailableTimePeriods(() => {
        const res = getParsedAvailableTimePeriods(
          schedule,
          currentCalendarTimezone,
        );

        if (res.length) {
          setActiveIndex(0);
        }

        return res;
      });
    }
  }, [schedule, currentCalendarTimezone, show]);

  const handleHide = useCallback(() => {
    // close confirm modal
    closeConfirmCancelModal();
    setActiveIndex(null);
    setError(undefined);
    setBusy(false);
    // reset to the initial state
    setParsedAvailableTimePeriods(
      getParsedAvailableTimePeriods(schedule, currentCalendarTimezone),
    );
    ref.current!.reset();
    // close
    return onHide();
  }, [closeConfirmCancelModal, onHide, schedule, currentCalendarTimezone]);

  // when changed, clear error
  useEffect(() => {
    setError(undefined);
  }, [parsedAvailableTimePeriods]);

  const isDirty = useMemo(
    () => !isEqual(getAscendingSchedule(parsedAvailableTimePeriods), schedule),
    [parsedAvailableTimePeriods, schedule],
  );

  const save = useCallback(async () => {
    closeConfirmSaveModal();

    // call api
    setBusy(true);
    try {
      const nextSchedule = getAscendingSchedule(parsedAvailableTimePeriods);

      const res = await postSchedule(nextSchedule);
      setParsedAvailableTimePeriods(
        getParsedAvailableTimePeriods(res, currentCalendarTimezone),
      );
      if (setSchedule) {
        setSchedule(res);
      }
      handleHide();
      // show saved tag
      setSavedTagProps({
        show: true,
        message: messages.availabilitySaved,
      });
    } catch (error) {
      setError(error);
    } finally {
      setBusy(false);
    }
  }, [
    closeConfirmSaveModal,
    handleHide,
    parsedAvailableTimePeriods,
    setSavedTagProps,
    setSchedule,
    currentCalendarTimezone,
  ]);

  const handleApply = useCallback(() => {
    setError(undefined);
    // validate
    const validity = ref.current!.checkValidity();

    // if has error, stop and show error
    if (!validity) {
      // if has error, stop and show error
      return;
    }
    if (isDirty) {
      openConfirmSaveModal();
    } else {
      handleHide();
    }
  }, [isDirty, openConfirmSaveModal, handleHide]);

  const handleDelete = useCallback(
    (i: number) => {
      if (i === null) {
        return;
      }

      if (activeIndex === i) {
        setActiveIndex(null);
      }

      setParsedAvailableTimePeriods((s) => [
        ...s.slice(0, i),
        ...s.slice(i + 1),
      ]);
    },
    [activeIndex],
  );

  const handleCancel = useCallback(() => {
    if (isDirty) {
      return openConfirmCancelModal();
    }
    return handleHide();
  }, [handleHide, isDirty, openConfirmCancelModal]);

  return (
    <>
      <ScheduleEditor
        ref={ref}
        canApply={!busy}
        canCancel={!busy}
        activeIndex={activeIndex}
        error={error}
        onApply={handleApply}
        onChange={(item) => {
          if (activeIndex !== null) {
            setParsedAvailableTimePeriods((s) => [
              ...s.slice(0, activeIndex),
              item,
              ...s.slice(activeIndex + 1),
            ]);
          }
        }}
        onCopy={(i) => {
          setParsedAvailableTimePeriods((s) => {
            const nextState = [...s.slice(0, i), { ...s[i] }, ...s.slice(i)];
            setActiveIndex(i + 1);
            return nextState;
          });
        }}
        onCreate={() =>
          setParsedAvailableTimePeriods((s) => {
            const nextState = [
              ...s,
              createParsedAvailableTimePeriod(currentCalendarTimezone),
            ];
            setActiveIndex(nextState.length - 1);
            return nextState;
          })
        }
        onDelete={handleDelete}
        onHide={handleCancel}
        onSelect={setActiveIndex}
        value={parsedAvailableTimePeriods}
        show={show}
        timezone={currentCalendarTimezone}
        analyticsPageName={analyticsPageName}
      />
      {/* confirm modal */}
      <ConfirmModal
        labelTitle={messages.areYouSure}
        children={messages.ifYouClose}
        labelConfirm={"OK"}
        hideCancel={true}
        show={showConfirmCancelModal}
        onCancel={closeConfirmCancelModal}
        onConfirm={handleHide}
      />
      <ConfirmModal
        labelTitle={messages.readyToUpdateYourAvailability}
        labelCancel={messages.noCancel}
        labelConfirm={messages.yesUpdate}
        show={showConfirmSaveModal}
        onCancel={closeConfirmSaveModal}
        onConfirm={save}
      >
        {messages.updateAvailabilityConfirmation}
      </ConfirmModal>
    </>
  );
};

export default ScheduleEditorContainer;
